import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDocument } from '../api/hooks';
import { buildDocHtml } from './docHtml';
import { previewPdf, printToPdf } from './printToPdf';
import { safeFileName } from './safeFileName';

/**
 * Editace uloženého dokumentu z archivu.
 *
 * Formulářové stránky (Faktura, Objednávka, Protokoly…) ukládají svůj stav do
 * `data_json`. Tento hook přečte `?id=N` z URL, načte daný záznam přes
 * `/api/documents/{id}` a JEDNORÁZOVĚ předá jeho `data_json` callbacku `onLoad`,
 * aby formulář předvyplnil svůj stav.
 *
 * Vrácené `editId` se předá do `handleSave` (`saveDoc.mutateAsync({ id: editId, … })`),
 * takže „Uložit" provede PUT a přepíše původní záznam místo vytvoření kopie.
 *
 * Navíc: pokud je v URL `?pdf=preview` (náhled z archivu) nebo `?pdf=download`,
 * po předvyplnění a vykreslení dokumentu se automaticky vygeneruje PDF —
 * náhled se zobrazí inline v této záložce, download se stáhne.
 *
 * `?pdf=html` — stránka běží ve skrytém iframe archivu (tlačítko „@ Poslat
 * e-mailem"): po vykreslení pošle HTML dokumentu rodiči přes postMessage
 * (`DOC_HTML_MESSAGE`) a PDF pak vygeneruje server při odeslání.
 */
export function useDocEditor(onLoad: (data: Record<string, unknown>) => void) {
  const [params] = useSearchParams();
  const idStr = params.get('id');
  const editId = idStr && /^\d+$/.test(idStr) ? Number(idStr) : null;
  const pdfMode = params.get('pdf'); // 'preview' | 'download' | 'html' | null
  const { data: doc, isLoading } = useDocument(editId);

  // Předvyplnit jednou pro každý záznam — onLoad sahá na setState settery, které
  // jsou stabilní. Klíčuje se podle id, aby šlo z tlačítka „Uložené" přepnout
  // na jiný záznam (`?id=5` → `?id=6`) bez remountu stránky.
  const loadedRef = useRef<number | null>(null);
  useEffect(() => {
    if (!doc || loadedRef.current === doc.id) return;
    loadedRef.current = doc.id;
    onLoad((doc.data_json ?? {}) as Record<string, unknown>);

    if (pdfMode === 'preview' || pdfMode === 'download') {
      void autoPdf(doc.title || 'dokument', pdfMode);
    } else if (pdfMode === 'html') {
      void postDocHtml(doc.id);
    }
  }, [doc, onLoad, pdfMode]);

  return { editId, isLoadingDoc: !!editId && isLoading };
}

export const DOC_HTML_MESSAGE = 'sd-doc-html';

export type DocHtmlMessage =
  | { type: typeof DOC_HTML_MESSAGE; id: number; ok: true; html: string }
  | { type: typeof DOC_HTML_MESSAGE; id: number; ok: false; error: string };

/** Režim `?pdf=html`: vykreslený dokument → HTML → rodičovské okno (archiv). */
async function postDocHtml(id: number): Promise<void> {
  const send = (msg: DocHtmlMessage) => window.parent.postMessage(msg, window.location.origin);
  try {
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(resolve, 400))),
    );
    const root = document.querySelector<HTMLElement>('[data-doc-page]');
    if (!root) throw new Error('Dokument se nevykreslil.');
    send({ type: DOC_HTML_MESSAGE, id, ok: true, html: await buildDocHtml(root) });
  } catch (e) {
    send({ type: DOC_HTML_MESSAGE, id, ok: false, error: e instanceof Error ? e.message : 'Chyba vykreslení.' });
  }
}

/** Počká na vykreslení předvyplněných hodnot a vygeneruje PDF (náhled/stažení). */
async function autoPdf(title: string, mode: 'preview' | 'download'): Promise<void> {
  const overlay = showOverlay(
    mode === 'preview' ? 'Generuji náhled PDF…' : 'Generuji PDF…',
  );
  try {
    // Dvojitý rAF + krátká pauza → React stihne commitnout předvyplněný stav do DOM.
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(resolve, 400))),
    );
    const root = document.querySelector<HTMLElement>('[data-doc-page]');
    if (!root) {
      overlay.remove();
      return;
    }
    const html = await buildDocHtml(root);
    const filename = safeFileName(title);
    if (mode === 'download') {
      await printToPdf(filename, html);
      overlay.remove();
    } else {
      // previewPdf přepne záložku na zobrazení PDF — overlay zmizí s navigací.
      await previewPdf(filename, html);
    }
  } catch {
    overlay.remove();
  }
}

/** Lehký celostránkový overlay po dobu generování PDF (bez závislosti na Reactu). */
function showOverlay(text: string): HTMLElement {
  const el = document.createElement('div');
  el.setAttribute('data-pdf-overlay', '');
  el.style.cssText =
    'position:fixed;inset:0;z-index:9999;display:flex;align-items:center;justify-content:center;' +
    'background:rgba(12,18,28,0.72);color:#fff;font:600 15px/1.4 Inter,system-ui,sans-serif;' +
    'backdrop-filter:blur(2px);';
  el.textContent = text;
  document.body.appendChild(el);
  return el;
}
